/**
 * Custom hook for managing project members
 * 
 * Project members are assigned a single role per project.
 * Operations are one-at-a-time (add one, remove one, update one role).
 */
import { useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import { useAuth } from "contexts/AuthContext";
import { useUserManagement } from "hooks";
import {
  listProjectMembers,
  addProjectMember,
  updateProjectMemberRole,
  removeProjectMember,
  transferProjectAdmin,
} from "services/pmService";

/**
 * Hook for managing project members
 * @param {string} projectId - The project ID
 * @returns {Object} - { members, loading, error, fetchMembers, addMember, updateMemberRole, removeMember }
 */
export default function useProjectMembers(projectId) {
  const { user } = useAuth();
  const { getUserById } = useUserManagement();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Handle axios errors
  const handleAxiosErrors = useCallback((err, action = "operation") => {
    let message = "An unexpected error occurred.";
    if (err.response) {
      message =
        err.response.data?.message ||
        `Failed to ${action}: ${err.response.statusText}`;
    } else if (err.request) {
      message =
        "No response from the server. Please check your internet connection.";
    } else {
      message = err.message || "Something went wrong.";
    }
    setError(message);
    toast.error(message);
    console.error(`${action} error:`, err);
  }, []);

  // Enrich member with user profile data
  const enrichMemberWithUser = useCallback(async (member) => {
    try {
      const userProfile = await getUserById(member.userId);
      return {
        ...member,
        user: {
          id: member.userId,
          name: userProfile?.fullName || userProfile?.firstName || "Unknown User",
          email: userProfile?.email || "",
          avatar: userProfile?.profilePhoto || null,
        },
      };
    } catch (error) {
      console.error("Error fetching user profile:", error);
      return {
        ...member,
        user: {
          id: member.userId,
          name: "Unknown User",
          email: "",
          avatar: null,
        },
      };
    }
  }, [getUserById]);

  // Fetch all members for the project
  const fetchMembers = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await listProjectMembers(projectId);
      // Enrich each member with user profile data
      const enrichedMembers = await Promise.all(
        (data || []).map((member) => enrichMemberWithUser(member))
      );
      setMembers(enrichedMembers);
    } catch (error) {
      handleAxiosErrors(error, "fetch project members");
    } finally {
      setLoading(false);
    }
  }, [projectId, handleAxiosErrors, enrichMemberWithUser]);

  // Add a single member to the project
  const addMember = useCallback(
    async (payload) => {
      if (!projectId) return null;
      setLoading(true);
      setError(null);
      try {
        const newMember = await addProjectMember(projectId, payload, user?.id);
        // Enrich the new member with user profile data
        const enrichedMember = await enrichMemberWithUser(newMember);
        setMembers((prev) => [...prev, enrichedMember]);
        toast.success("Member added successfully");
        return enrichedMember;
      } catch (error) {
        // Check for conflict (duplicate member)
        if (error.response?.status === 409) {
          toast.error("This user is already a member of this project");
        } else {
          handleAxiosErrors(error, "add project member");
        }
        return null;
      } finally {
        setLoading(false);
      }
    },
    [projectId, handleAxiosErrors, user?.id, enrichMemberWithUser]
  );

  // Update a member's role
  const updateMemberRole = useCallback(
    async (memberId, payload) => {
      if (!projectId || !memberId) return null;
      setLoading(true);
      setError(null);
      try {
        const updatedMember = await updateProjectMemberRole(
          projectId,
          memberId,
          payload,
          user?.id
        );
        // Enrich the updated member with user profile data
        const enrichedMember = await enrichMemberWithUser(updatedMember);
        setMembers((prev) =>
          prev.map((member) =>
            member.id === memberId ? enrichedMember : member
          )
        );
        toast.success("Member role updated successfully");
        return enrichedMember;
      } catch (error) {
        handleAxiosErrors(error, "update member role");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [projectId, handleAxiosErrors, user?.id, enrichMemberWithUser]
  );

  // Remove a single member from the project
  const removeMember = useCallback(
    async (memberId) => {
      if (!projectId || !memberId) return false;
      setLoading(true);
      setError(null);
      try {
        await removeProjectMember(projectId, memberId, user?.id);
        setMembers((prev) => prev.filter((member) => member.id !== memberId));
        toast.success("Member removed successfully");
        return true;
      } catch (error) {
        handleAxiosErrors(error, "remove project member");
        return false;
      } finally {
        setLoading(false);
      }
    },
    [projectId, handleAxiosErrors, user?.id]
  );

  // Transfer project admin to another active member
  const makeAdmin = useCallback(
    async (targetUserId) => {
      if (!projectId || !targetUserId) return false;
      setLoading(true);
      setError(null);
      try {
        await transferProjectAdmin(projectId, { userId: targetUserId }, user?.id);
        // Refresh members to get updated admin status
        await fetchMembers();
        toast.success("Project admin transferred successfully");
        return true;
      } catch (error) {
        handleAxiosErrors(error, "transfer project admin");
        return false;
      } finally {
        setLoading(false);
      }
    },
    [projectId, handleAxiosErrors, user?.id]
  );

  // Auto-fetch members when projectId changes
  useEffect(() => {
    if (projectId) {
      fetchMembers();
    }
  }, [projectId, fetchMembers]);

  return {
    members,
    loading,
    error,
    fetchMembers,
    addMember,
    updateMemberRole,
    removeMember,
    makeAdmin,
  };
}
